import React from "react";
import { enqueueSnackbar } from "notistack";
import { Modal, Tooltip, Button, Segmented, Tag } from "antd";
import { generateMalimaxOneForShowbyId } from "../../../../libs/apis";
import { MalimaxOneI, MalimaxThreeI, MalimaxTwoI } from "../../../../libs/interfaces";
import { Box } from "@mui/material";
import type { Dayjs } from "dayjs";
import {
  CloseOutlined,
  FundViewOutlined,
  InfoCircleOutlined,
  PictureOutlined,
} from "@ant-design/icons";
import { OneCard, OneVideo } from "../../../../libs/shared";
import "./show-container.css";

/* ─────────────────────────────────────────────── */
/*  Interfaces                                     */
/* ─────────────────────────────────────────────── */
interface OnePhotoI {
  src: string | number | boolean | Dayjs | null | undefined;
  alt: string;
  showAlt?: boolean;
  type?: any;
}

interface OneCardI {
  title: string;
  photos: OnePhotoI[];
  value?: string;
  noLine?: boolean;
  type: any;
}

interface ShowContainerPropsI {
  id: number;
  isError?: boolean;
}

/* ─────────────────────────────────────────────── */
/*  Trigger button                                 */
/* ─────────────────────────────────────────────── */
export const ShowContainer = ({ id, isError }: ShowContainerPropsI) => {
  const [open, setOpen] = React.useState(false);
  const [showContainer, setShowContainer] = React.useState(false);

  React.useEffect(() => {
    if (showContainer) setOpen(true);
  }, [showContainer]);

  const handleClose = () => {
    setShowContainer(false);
    setOpen(false);
  };

  return (
    <>
      <Tooltip arrow={false} title="Show">
        <Button
          size="small"
          type="dashed"
          onClick={() => setShowContainer(true)}
          icon={<FundViewOutlined />}
        />
      </Tooltip>
      {showContainer && (
        <ModalContainer open={open} onClose={handleClose} id={id} isError={isError} />
      )}
    </>
  );
};

/* ─────────────────────────────────────────────── */
/*  Shared field component                         */
/* ─────────────────────────────────────────────── */
const Field = ({
  label,
  value,
  full,
}: {
  label: string;
  value?: string | null;
  full?: boolean;
}) => (
  <div className={`info-block${full ? " full-width" : ""}`}>
    <div className="label">{label}</div>
    <div className={`value${!value ? " empty" : ""}`}>{value || "—"}</div>
  </div>
);

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <div className="form-section-title">{children}</div>
);

const TimeBadges = ({
  hourInit,
  hourEnd,
  hourSave,
  timeStampSave,
}: {
  hourInit?: string;
  hourEnd?: string;
  hourSave?: string | null;
  timeStampSave?: Date | null;
}) => (
  <div className="time-row">
    <div className="time-badge">
      <span className="t-label">Hora inicio</span>
      <span className="t-value">{hourInit || "—"}</span>
    </div>
    <div className="time-badge">
      <span className="t-label">Hora fin</span>
      <span className="t-value">{hourEnd || "—"}</span>
    </div>
    <div className="time-badge">
      <span className="t-label">Guardado</span>
      <span className="t-value">
        {hourSave
          ? hourSave
          : timeStampSave
          ? new Date(timeStampSave as any).toLocaleTimeString()
          : "—"}
      </span>
    </div>
  </div>
);

/* ─────────────────────────────────────────────── */
/*  Combined form panel                            */
/* ─────────────────────────────────────────────── */
const FormPanel = ({
  one,
  two,
  three,
}: {
  one?: MalimaxOneI;
  two?: MalimaxTwoI;
  three?: MalimaxThreeI;
}) => (
  <div className="info-grid">
    {/* ── PROCESO 1 ── */}
    {one && (
      <>
        <div className="process-header">
          <span className="process-badge p1">Proceso 1 — Vacío</span>
        </div>
        <TimeBadges
          hourInit={one.hourInit}
          hourEnd={one.hourEnd}
          hourSave={one.hourSave}
          timeStampSave={one.timeStampSave}
        />
        <SectionTitle>General</SectionTitle>
        <Field label="Cliente" value={one.client} />
        <Field label="ID Cliente" value={one.clientIdentification} />
        <Field label="Tipo Servicio" value={one.typeService} />
        <Field label="Tipo Contenedor" value={one.typeContainer} />
        <Field label="Fecha" value={one.date} />
        <Field label="Ciudad" value={one.city} />
        <Field label="Puerto de entrada" value={one.entryPort} />
        <Field label="Lugar de trabajo" value={one.workplace} />
        <Field label="Dirección" value={one.address} />
        <Field label="Almacén" value={one.storageName} />
        <Field label="Coordenadas" value={one.coordinates} />
        <SectionTitle>Transporte</SectionTitle>
        <Field label="Empresa transporte" value={one.companyTransport} />
        <Field label="Placa vehículo" value={one.plateVehicle} />
        <Field label="Conductor" value={one.driverName} />
        <Field label="CI Conductor" value={one.driverIdentification} />
        <SectionTitle>Personas</SectionTitle>
        <Field label="Inspeccionado por" value={one.inspectedBy} />
        <Field label="Supervisor Exportador" value={one.exporterSupervisor} />
        <Field label="ID Supervisor Exportador" value={one.exporterSupervisorIdentification} />
        <Field label="Asociado" value={one.associated} />
        <Field label="ID Asociado" value={one.associatedIdentification} />
        <Field label="Otros" value={one.others} />
        <Field label="ID Otros" value={one.othersIdentification} />
        {one.observation && (
          <>
            <SectionTitle>Observaciones</SectionTitle>
            <Field label="Observación" value={one.observation} full />
          </>
        )}
      </>
    )}

    {/* ── PROCESO 2 ── */}
    {two && (
      <>
        <div className="process-header">
          <span className="process-badge p2">Proceso 2 — Carga</span>
        </div>
        <TimeBadges
          hourInit={two.hourInit}
          hourEnd={two.hourEnd}
          hourSave={two.hourSave}
          timeStampSave={two.timeStampSave}
        />
        <SectionTitle>Producto</SectionTitle>
        <Field label="Cliente" value={two.client} />
        <Field label="Producto" value={two.product} />
        <Field label="Presentación" value={two.presentation} />
        <Field label="N° Pallets" value={two.numberPallet} />
        <Field label="N° Presentaciones" value={two.numberPresentation} />
        <Field label="N° Muestreo" value={two.numberSampling} />
        <Field label="Coordenadas" value={two.coordinates} />
        {!!two.peoplePresent?.length && (
          <>
            <SectionTitle>Personas Presentes en la Operativa</SectionTitle>
            {two.peoplePresent.map((p, i) => (
              <Field
                key={i}
                label={`Persona ${i + 1}`}
                value={[p.name, p.identification && `C.I. ${p.identification}`, p.position]
                  .filter(Boolean)
                  .join(" — ")}
                full
              />
            ))}
          </>
        )}
        {two.observation && (
          <>
            <SectionTitle>Observaciones</SectionTitle>
            <Field label="Observación" value={two.observation} full />
          </>
        )}
      </>
    )}

    {/* ── PROCESO 3 ── */}
    {three && (
      <>
        <div className="process-header">
          <span className="process-badge p3">Proceso 3 — Sellado</span>
        </div>
        <TimeBadges
          hourInit={three.hourInit}
          hourEnd={three.hourEnd}
          hourSave={three.hourSave}
          timeStampSave={three.timeStampSave}
        />
        <SectionTitle>General</SectionTitle>
        <Field label="Cliente" value={three.client} />
        <Field label="ID Cliente" value={three.clientIdentification} />
        <Field label="Puerto de entrada" value={three.entryPort} />
        <Field label="Coordenadas" value={three.coordinates} />
        <Field label="Coordenadas Panorámica" value={three.containerPanoramicCoordinates} />
        <SectionTitle>Videos</SectionTitle>
        <Field label="Comentario Video Puerta" value={three.doorVideoComment} />
        <Field label="Comentario Video Maquinaria" value={three.engineryVideoComment} />
        {three.observation && (
          <>
            <SectionTitle>Observaciones</SectionTitle>
            <Field label="Observación" value={three.observation} full />
          </>
        )}
      </>
    )}
  </div>
);

/* ─────────────────────────────────────────────── */
/*  Photo section helper                           */
/* ─────────────────────────────────────────────── */
const PhotoSection = ({
  title,
  type,
  cards,
  totalPhotos,
}: {
  title: string;
  type: string;
  cards: OneCardI[];
  totalPhotos: string[];
}) => {
  const filtered = cards.filter((el) => el.type === type);
  if (!filtered.length) return null;
  return (
    <>
      <div className="section-header">
        <h3>{title}</h3>
      </div>
      <Box
        sx={{
          display: "grid",
          gap: 1.5,
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
        }}
      >
        {filtered.map((el, i) => (
          <OneCard data={el} key={`${el.title}-${i}`} lasFotos={totalPhotos} />
        ))}
      </Box>
    </>
  );
};

/* ─────────────────────────────────────────────── */
/*  Per-process photo panels                       */
/* ─────────────────────────────────────────────── */
const PhotosPanelOne = ({
  cards,
  totalPhotos,
  videos,
}: {
  cards: OneCardI[];
  totalPhotos: string[];
  videos: OnePhotoI[];
}) => (
  <Box>
    <PhotoSection title="Fotos Externas" type="external" cards={cards} totalPhotos={totalPhotos} />
    <PhotoSection title="Fotos Internas" type="internal" cards={cards} totalPhotos={totalPhotos} />
    <PhotoSection title="Fotos Rumbo a finca" type="exit" cards={cards} totalPhotos={totalPhotos} />
    <PhotoSection title="Fotos Adicionales" type="additional" cards={cards} totalPhotos={totalPhotos} />
    {videos.some((v) => v.src) && (
      <>
        <div className="section-header"><h3>Videos</h3></div>
        <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}>
          {videos.map((el) => <OneVideo {...el} key={`${el.alt}-video`} />)}
        </Box>
      </>
    )}
  </Box>
);

const PhotosPanelTwo = ({
  cards,
  totalPhotos,
  videos,
}: {
  cards: OneCardI[];
  totalPhotos: string[];
  videos: OnePhotoI[];
}) => (
  <Box>
    <PhotoSection title="Proceso de Carga" type="validation" cards={cards} totalPhotos={totalPhotos} />
    
  </Box>
);

const PhotosPanelThree = ({
  cards,
  totalPhotos,
  videos,
}: {
  cards: OneCardI[];
  totalPhotos: string[];
  videos: OnePhotoI[];
}) => (
  <Box>
    <PhotoSection title="Naviera" type="naviera" cards={cards} totalPhotos={totalPhotos} />
    <PhotoSection title="Exportador" type="exporter" cards={cards} totalPhotos={totalPhotos} />
    <PhotoSection title="Otras" type="other" cards={cards} totalPhotos={totalPhotos} />
    <PhotoSection title="GPS" type="gps" cards={cards} totalPhotos={totalPhotos} />
    <PhotoSection title="Fotos Adicionales" type="additional" cards={cards} totalPhotos={totalPhotos} />
   
  </Box>
);

/* ─────────────────────────────────────────────── */
/*  Segmented tab label helper                     */
/* ─────────────────────────────────────────────── */
const TabLabel = ({
  icon,
  label,
  count,
  color,
}: {
  icon: React.ReactNode;
  label: string;
  count?: number;
  color?: string;
}) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
    {icon}
    {label}
    {!!count && (
      <span
        style={{
          background: color || "#25D228",
          color: "white",
          borderRadius: 10,
          padding: "0 6px",
          fontSize: "0.68rem",
          lineHeight: "1.4",
        }}
      >
        {count}
      </span>
    )}
  </span>
);

/* ─────────────────────────────────────────────── */
/*  Modal                                          */
/* ─────────────────────────────────────────────── */
interface ModalContainerPropsI {
  open: boolean;
  onClose: () => void;
  id: number;
  isError?: boolean;
}

export const ModalContainer = ({ open, onClose, id, isError }: ModalContainerPropsI) => {
  const [isLoading, setLoading] = React.useState(true);
  const [rawData, setRawData] = React.useState<{
    malimaxOne?: MalimaxOneI;
    malimaxTwo?: MalimaxTwoI;
    malimaxThree?: MalimaxThreeI;
  }>({});

  const [cardsOne, setCardsOne] = React.useState<OneCardI[]>([]);
  const [videosOne, setVideosOne] = React.useState<OnePhotoI[]>([]);
  const [photosOne, setPhotosOne] = React.useState<string[]>([]);

  const [cardsTwo, setCardsTwo] = React.useState<OneCardI[]>([]);
  const [videosTwo, setVideosTwo] = React.useState<OnePhotoI[]>([]);
  const [photosTwo, setPhotosTwo] = React.useState<string[]>([]);

  const [cardsThree, setCardsThree] = React.useState<OneCardI[]>([]);
  const [videosThree, setVideosThree] = React.useState<OnePhotoI[]>([]);
  const [photosThree, setPhotosThree] = React.useState<string[]>([]);

  const [tab, setTab] = React.useState<string>("form");

  React.useEffect(() => {
    handleGetData();
    return () => {
      setRawData({});
      setCardsOne([]); setCardsTwo([]); setCardsThree([]);
      setLoading(true);
      setTab("form");
    };
  }, []);

  const handleGetData = async () => {
    try {
      const { data } = await generateMalimaxOneForShowbyId(id);
      if (!data) return;

      setRawData({
        malimaxOne: data.malimaxOne,
        malimaxTwo: data.malimaxTwo,
        malimaxThree: data.malimaxThree,
      });

      if (data.malimaxOne) processOne(data.malimaxOne);
      if (data.malimaxTwo) processTwo(data.malimaxTwo);
      if (data.malimaxThree) processThree(data.malimaxThree);

      setLoading(false);
    } catch (err: any) {
      enqueueSnackbar(err.toString(), { variant: "error" });
      setLoading(false);
    }
  };

  const processOne = (d: MalimaxOneI) => {
    const lasFotos: string[] = [];
    const arrCards: any[] = [];

    const externalPhotos = [
      { title: "Foto EIR", src: d.emptyEirPhoto, alt: "EIR", comment: "" },
      { title: "Foto Dcto. Inspección Prev.", src: d.emptyPreviousInspectionDocumentPhoto, alt: "Documento Inspección Previa", comment: "" },
      { title: "Foto Placa", src: d.emptyPlatePhoto, alt: "Placa", comment: "" },
      { title: "Foto CI Conductor", src: d.emptyDriverIdentificationPhoto, alt: "Identificación del Conductor", comment: "" },
      { title: "Foto Panorámica", src: d.emptyPanoramicPhoto, alt: "Panorámica", comment: d.emptyPanoramicComment },
      { title: "Foto Sello Adicional", src: d.emptyAditionalStampPhoto, alt: "Sello Adicional", comment: d.emptyAditionalStampComment },
      { title: "Foto Sello Naviera", src: d.emptyStampNavieraPhoto, alt: "Sello Naviera", comment: d.emptyStampNavieraComment },
      { title: "Foto Otro Sello", src: d.emptyOtherStampPhoto, alt: "Otro Sello", comment: d.emptyOtherStampComment },
      { title: "Foto Candado Satelital", src: d.emptySatelliteLockPhoto, alt: "Candado Satelital", comment: d.emptySatelliteLockComment },
      { title: "Foto Sello Candado Gps", src: d.emptySatelliteLockStampPhoto, alt: "Sello Candado Satelital", comment: d.emptySatelliteLockStampComment },
      { title: "Foto Lado Derecho", src: d.emptySideRightPhoto, alt: "Lado Derecho", comment: "" },
      { title: "Foto Lado Izquierdo", src: d.emptySideLeftPhoto, alt: "Lado Izquierdo", comment: "" },
      { title: "Foto Techo Interno", src: d.emptySideUpPhoto, alt: "Techo Interno", comment: "" },
      { title: "Foto Piso Interno", src: d.emptySideDownPhoto, alt: "Piso Interno", comment: "" },
      { title: "Foto Frontal", src: d.emptyFrontPhoto, alt: "Frontal", comment: "" },
      { title: "Foto Posterior", src: d.emptyRearPhoto, alt: "Posterior", comment: "" },
    ];

    externalPhotos.forEach((photo) => {
      arrCards.push({ title: photo.title, noLine: true, photos: [{ src: photo.src, alt: photo.alt, showAlt: false }], value: photo.comment, type: "external" });
      lasFotos.push(photo.src as string);
    });

    type InternalPhotoKeys = `emptyInternalPhoto${1 | 2 | 3 | 4 | 5 | 6}`;
    type InternalCommentKeys = `emptyInternalComment${1 | 2 | 3 | 4 | 5 | 6}`;

    const internalPhotos = [
      { title: "Foto Piso", src: d.emptyFloorPhoto, alt: "Piso", comment: d.emptyFloorComment },
      { title: "Foto Techo", src: d.emptyRoofPhoto, alt: "Techo", comment: d.emptyRoofComment },
      { title: "Foto Cubierta del Espejo", src: d.emptyMirrorCoverPhoto, alt: "Cubierta del Espejo", comment: d.emptyMirrorCoverComment },
    ];
    for (let i = 1; i <= 6; i++) {
      internalPhotos.push({
        title: `Foto Interna ${i}`,
        src: d[`emptyInternalPhoto${i}` as InternalPhotoKeys],
        alt: `Interna ${i}`,
        comment: d[`emptyInternalComment${i}` as InternalCommentKeys],
      });
    }
    internalPhotos.forEach((photo) => {
      arrCards.push({ title: photo.title, noLine: true, photos: [{ src: photo.src, alt: photo.alt, showAlt: false }], value: photo.comment, type: "internal" });
      lasFotos.push(photo.src as string);
    });

    const exitPhotos = [
      { title: "Foto Panorámica", src: d.exitPanoramicPhoto, alt: "Panorámica", comment: d.exitPanoramicComment },
      { title: "Foto Sellado Temporal", src: d.exitTemporarySealingPhoto, alt: "Sellado Temporal", comment: d.exitTemporarySealingComment },
    ];
    exitPhotos.forEach((photo) => {
      arrCards.push({ title: photo.title, noLine: true, photos: [{ src: photo.src, alt: photo.alt, showAlt: false }], value: photo.comment, type: "exit" });
      lasFotos.push(photo.src as string);
    });

    (d.images ?? []).forEach((img, i) => {
      if (!img?.src) return;
      arrCards.push({ title: `Foto Adicional ${i + 1}`, noLine: true, photos: [{ src: img.src, alt: img.comment ?? "", showAlt: false }], value: img.comment ?? "", type: "additional" });
      lasFotos.push(img.src as string);
    });

    setPhotosOne(lasFotos);
    setCardsOne(arrCards);
    setVideosOne([
      // { src: d.emptyInternalVideo, alt: "Video Interno", type: "video" },
      { src: d.exitDoorVideo, alt: "Video Cont. Vacio", type: "video" },
    ]);
  };

  const processTwo = (d: MalimaxTwoI) => {
    const arrCards: OneCardI[] = d.images.map((photo) => ({
      title: "Fotos",
      noLine: true,
      photos: [{ src: photo.src, alt: photo.comment, type: "validation" }],
      value: photo.comment ?? "",
      type: "validation",
    }));
    setPhotosTwo(d.images.map((p) => p.src));
    setCardsTwo(arrCards);
    setVideosTwo([
      // { src: d.videoOne, alt: "Video 1", type: "video-container" as any },
      // { src: d.videoTwo, alt: "Video 2", type: "video-container" as any },
    ]);
  };

  const processThree = (d: MalimaxThreeI) => {
    const arrCards: OneCardI[] = [
      { title: "Foto Panorámica",         photos: [{ src: d.containerPanoramicPhoto, alt: "Panorámica",        showAlt: true }], value: d.containerPanoramicComment, type: "naviera"  },
      { title: "Foto Botella Naviera",    photos: [{ src: d.navieraBottlePhoto,      alt: "Botella Naviera",   showAlt: true }], value: d.navieraBottleComment,      type: "naviera"  },
      { title: "Foto Etiqueta Naviera",   photos: [{ src: d.navieraLabelPhoto,       alt: "Etiqueta Naviera",  showAlt: true }], value: d.navieraLabelComment,       type: "naviera"  },
      { title: "Foto Cable Naviera",      photos: [{ src: d.navieraWirePhoto,        alt: "Cable Naviera",     showAlt: true }], value: d.navieraWireComment,        type: "naviera"  },
      { title: "Foto Botella Exportador", photos: [{ src: d.exporterBottlePhoto,     alt: "Botella Exportador",showAlt: true }], value: d.exporterBottleComment,     type: "exporter" },
      { title: "Foto Etiqueta Exportador",photos: [{ src: d.exporterLabelPhoto,      alt: "Etiqueta Exportador",showAlt:true }], value: d.exporterLabelComment,      type: "exporter" },
      { title: "Foto Cable Exportador",   photos: [{ src: d.exporterWirePhoto,       alt: "Cable Exportador",  showAlt: true }], value: d.exporterWireComment,       type: "exporter" },
      { title: "Foto Botella Otro",       photos: [{ src: d.otherBottlePhoto,        alt: "Botella Otro",      showAlt: true }], value: d.otherBottleComment,        type: "other"    },
      { title: "Foto Etiqueta Otro",      photos: [{ src: d.otherLabelPhoto,         alt: "Etiqueta Otro",     showAlt: true }], value: d.otherLabelComment,         type: "other"    },
      { title: "Foto Cable Otro",         photos: [{ src: d.otherWirePhoto,          alt: "Cable Otro",        showAlt: true }], value: d.otherWireComment,          type: "other"    },
      { title: "Foto GPS",                photos: [{ src: d.gpsPhoto,               alt: "GPS",               showAlt: true }], value: d.gpsComment,                type: "gps"      },
      { title: "Foto Sello GPS",          photos: [{ src: d.gpsStampPhoto,          alt: "Sello GPS",         showAlt: true }], value: d.gpsStampComment,           type: "gps"      },
    ];
    (d.images ?? []).forEach((img, i) => {
      if (!img?.src) return;
      arrCards.push({ title: `Foto Adicional ${i + 1}`, photos: [{ src: img.src, alt: "Adicional", showAlt: true }], value: img.comment ?? "", type: "additional" });
    });
    const lasFotos: string[] = [];
    arrCards.forEach((c) => c.photos.forEach((p) => lasFotos.push(p.src as string)));
    setPhotosThree(lasFotos);
    setCardsThree(arrCards);
    setVideosThree([
      // { src: d.engineryVideo, alt: "Video Maquinaria" },
      // { src: d.doorVideo,     alt: "Video Puerta" },
    ]);
  };

  /* ── tab availability & counts ── */
  const hasOne   = !!rawData.malimaxOne;
  const hasTwo   = !!rawData.malimaxTwo;
  const hasThree = !!rawData.malimaxThree;

  const countOne   = cardsOne.filter((c) => c.photos?.[0]?.src).length;
  const countTwo   = cardsTwo.filter((c) => c.photos?.[0]?.src).length;
  const countThree = cardsThree.filter((c) => c.photos?.[0]?.src).length;

  const container = rawData.malimaxOne?.container ?? rawData.malimaxTwo?.container ?? rawData.malimaxThree?.container ?? "";
  const statusWorkflow = rawData.malimaxOne?.statusWorkflow ?? rawData.malimaxThree?.statusWorkflow;
  const statusColor    = rawData.malimaxOne?.statusWorkflowColor ?? rawData.malimaxThree?.statusWorkflowColor;

  /* build segmented options dynamically */
  const segOptions = [
    {
      label: <TabLabel icon={<InfoCircleOutlined />} label="Información" />,
      value: "form",
    },
    ...(hasOne
      ? [{
          label: <TabLabel icon={<PictureOutlined />} label="Fotos P1" count={countOne} color="#0B0D0B" />,
          value: "p1",
        }]
      : []),
    ...(hasTwo
      ? [{
          label: <TabLabel icon={<PictureOutlined />} label="Fotos P2" count={countTwo} color="#0a6e2a" />,
          value: "p2",
        }]
      : []),
    ...(hasThree
      ? [{
          label: <TabLabel icon={<PictureOutlined />} label="Fotos P3" count={countThree} color="#25D228" />,
          value: "p3",
        }]
      : []),
  ];

  return (
    <Modal
      title={
        <p className="modal-header-gradient">
          {`CONTENEDOR N° ${container}`}
          {statusWorkflow && (
            <Tag
              color={statusColor || "#25D228"}
              style={{ marginLeft: 10, fontSize: "0.75rem", verticalAlign: "middle" }}
            >
              {statusWorkflow}
            </Tag>
          )}
        </p>
      }
      closeIcon={
        <CloseOutlined style={{ background: "red", color: "white", padding: "5px", borderRadius: "5px" }} />
      }
      footer={null}
      loading={isLoading}
      open={open}
      onClose={onClose}
      onCancel={onClose}
      width="96vw"
      centered
      zIndex={1602}
      destroyOnClose
      style={{ padding: 0, maxWidth: 1100 }}
    >
      {/* Segmented */}
      <Box sx={{ display: "flex", justifyContent: "center", mb: 1.5, mt: 0.5 }}>
        <Segmented
          className="modal-segmented"
          value={tab}
          onChange={(v) => setTab(v as string)}
          options={segOptions}
          style={{ fontWeight: 600 }}
        />
      </Box>

      {/* Content */}
      <Box sx={{ overflowY: "auto", maxHeight: "78vh", p: 1.5 }}>
        {tab === "form" && (
          <FormPanel
            one={rawData.malimaxOne}
            two={rawData.malimaxTwo}
            three={rawData.malimaxThree}
          />
        )}
        {tab === "p1" && hasOne && (
          <PhotosPanelOne cards={cardsOne} totalPhotos={photosOne} videos={videosOne} />
        )}
        {tab === "p2" && hasTwo && (
          <PhotosPanelTwo cards={cardsTwo} totalPhotos={photosTwo} videos={videosTwo} />
        )}
        {tab === "p3" && hasThree && (
          <PhotosPanelThree cards={cardsThree} totalPhotos={photosThree} videos={videosThree} />
        )}
      </Box>
    </Modal>
  );
};